const DEFAULT_PROFILE = Object.freeze({
  engine: "curtain.dynamic.v1",
  version: 1,
  category: "cloth",
  fidelity: "reference_informed",
  properties: {
    gravity: { value: [0, -9.81, 0], provenance: "simulated" },
    damping: { value: 0.84, provenance: "estimated" },
    airDrag: { value: 0.32, provenance: "estimated" },
    bendResistance: { value: 0.58, provenance: "estimated" },
    surfaceDensity: { value: 0.18, provenance: "estimated" },
    hemMassScale: { value: 1.35, provenance: "estimated" },
    maxDisplacement: { value: 0.22, provenance: "simulated" },
    attachment: { value: "top_track", provenance: "estimated" },
  },
});

const clamp = (value, min, max, fallback = 0) => {
  const number = Number(value);
  return Number.isFinite(number) ? Math.min(max, Math.max(min, number)) : fallback;
};

const vector = (value, fallback = [0, 0, 0], limit = 100) => {
  const raw = Array.isArray(value) && value.length === 3 ? value : fallback;
  return raw.map((item, index) => clamp(item, -limit, limit, fallback[index] || 0));
};

function profileValue(profile, key, fallback) {
  const value = profile?.properties?.[key]?.value;
  return value == null ? fallback : value;
}

export function normalizeCurtainSimulationProfile(profile = null) {
  const source = profile || DEFAULT_PROFILE;
  return {
    ...DEFAULT_PROFILE,
    ...source,
    properties: {
      ...DEFAULT_PROFILE.properties,
      ...(source?.properties || {}),
      gravity: {
        ...DEFAULT_PROFILE.properties.gravity,
        ...(source?.properties?.gravity || {}),
        value: vector(source?.properties?.gravity?.value, [0, -9.81, 0], 30),
      },
      damping: {
        ...DEFAULT_PROFILE.properties.damping,
        ...(source?.properties?.damping || {}),
        value: clamp(profileValue(source, "damping", 0.84), 0.05, 0.995, 0.84),
      },
      airDrag: {
        ...DEFAULT_PROFILE.properties.airDrag,
        ...(source?.properties?.airDrag || {}),
        value: clamp(profileValue(source, "airDrag", 0.32), 0, 1, 0.32),
      },
      bendResistance: {
        ...DEFAULT_PROFILE.properties.bendResistance,
        ...(source?.properties?.bendResistance || {}),
        value: clamp(profileValue(source, "bendResistance", 0.58), 0.02, 1, 0.58),
      },
      surfaceDensity: {
        ...DEFAULT_PROFILE.properties.surfaceDensity,
        ...(source?.properties?.surfaceDensity || {}),
        value: clamp(profileValue(source, "surfaceDensity", 0.18), 0.03, 1.5, 0.18),
      },
      hemMassScale: {
        ...DEFAULT_PROFILE.properties.hemMassScale,
        ...(source?.properties?.hemMassScale || {}),
        value: clamp(profileValue(source, "hemMassScale", 1.35), 0.5, 4, 1.35),
      },
      maxDisplacement: {
        ...DEFAULT_PROFILE.properties.maxDisplacement,
        ...(source?.properties?.maxDisplacement || {}),
        value: clamp(profileValue(source, "maxDisplacement", 0.22), 0.03, 0.6, 0.22),
      },
    },
  };
}

function reducedMotionPreferred() {
  try {
    return typeof window !== "undefined"
      && typeof window.matchMedia === "function"
      && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch {
    return false;
  }
}

export function createCurtainSimulationController(profile = null, options = {}) {
  let normalized = normalizeCurtainSimulationProfile(profile);
  const listeners = new Set();
  const reducedMotion = options.reducedMotion ?? reducedMotionPreferred();
  let time = 0;
  let wind = [0, 0, 0];
  let turbulence = 0;
  let impulse = [0, 0, 0];
  let revision = 0;

  const notify = () => {
    revision += 1;
    const snapshot = api.snapshot();
    for (const listener of listeners) {
      try { listener(snapshot); } catch {}
    }
  };

  const api = {
    get profile() { return normalized; },
    get revision() { return revision; },
    setProfile(next) {
      normalized = normalizeCurtainSimulationProfile(next);
      notify();
    },
    setWind(nextVector = [0, 0, 0], nextTurbulence = 0) {
      wind = vector(nextVector, [0, 0, 0], 100);
      turbulence = reducedMotion ? 0 : clamp(nextTurbulence, 0, 1, 0);
      notify();
      return api.snapshot();
    },
    applyImpulse(nextVector = [0, 0, 0]) {
      const incoming = vector(nextVector, [0, 0, 0], 1000);
      impulse = impulse.map((value, index) => clamp(value + incoming[index], -1000, 1000, 0));
      notify();
      return api.snapshot();
    },
    syncAuthoritativeState(state = {}) {
      wind = vector(state?.wind, wind, 100);
      turbulence = reducedMotion ? 0 : clamp(state?.turbulence, 0, 1, turbulence);
      impulse = vector(state?.impulse, impulse, 1000);
      notify();
      return api.snapshot();
    },
    applyAction(action) {
      const type = String(action?.type || "");
      if (type === "physics.set_wind") {
        api.setWind(action?.payload?.vector, action?.payload?.turbulence);
        return true;
      }
      if (type === "physics.apply_impulse") {
        api.applyImpulse(action?.payload?.vector);
        return true;
      }
      return false;
    },
    reset() {
      time = 0;
      wind = [0, 0, 0];
      turbulence = 0;
      impulse = [0, 0, 0];
      notify();
    },
    step(deltaSeconds = 1 / 60) {
      const delta = clamp(deltaSeconds, 0, 0.05, 1 / 60);
      time += delta;
      const damping = clamp(profileValue(normalized, "damping", 0.84), 0.05, 0.995, 0.84);
      const decay = Math.pow(Math.max(0.02, damping), delta * 60);
      impulse = impulse.map(value => Math.abs(value) < 0.0005 ? 0 : value * decay);
      return api.snapshot();
    },
    needsAnimation() {
      const impulseMagnitude = Math.hypot(...impulse);
      // A steady wind can settle to a static displaced shape. Only turbulence
      // or a decaying impulse needs an indefinitely continuing demand loop.
      return impulseMagnitude > 0.002 || (!reducedMotion && turbulence > 0.002);
    },
    snapshot() {
      return {
        engine: normalized.engine || "curtain.dynamic.v1",
        time,
        wind: [...wind],
        turbulence: reducedMotion ? 0 : turbulence,
        impulse: [...impulse],
        reducedMotion,
        revision,
        profile: normalized,
      };
    },
    subscribe(listener) {
      if (typeof listener !== "function") return () => {};
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
  return api;
}

export { DEFAULT_PROFILE };
